import { action, computed, flow, makeObservable, observable } from 'mobx';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { InsuranceQuoteFormStore } from './insuranceQuoteFormStore';
import { isNil } from '@roc/feature-utils';
import ResendQuoteStore from './resendQuoteStore';
import { FinalDocumentType, INSURANCE_STATUS_APPROVED } from '../util';
import _ from 'lodash';
import {
  InsuranceCarrierStore,
  InsuranceAgentDirectoryStore,
} from '@roc/feature-insurance-agents';

export abstract class InsuranceInformationBaseStore {
  public globalStore: GlobalStore;
  insuranceQuoteFormStore: InsuranceQuoteFormStore;
  resendElmsureQuoteStore: ResendQuoteStore;
  finalDocumentSelectionGridStore: GridStore;

  isSaveInsuranceQuoteActive: boolean;
  quotes: any[];
  finalChoiceQuotes: any[];
  propertiesMissingQuotes: string[];
  propertiesOptions: any[];
  currentQuote: any;
  isPdfPreviewModalOpen: boolean;
  pdfTitle: string;
  pdfData: any;
  uploadDocuments: boolean;
  viewDetails: boolean;
  selectQouteConfirm: boolean;
  loanId: string;
  taskId: string;
  loanDetails: any;
  resendElmsureQuoteModal: boolean;
  selectedRecords: any[];
  finalBinder: string;
  finalInvoice: string;
  finalElmsureBinder: string;
  finalElmsureInvoice: string;
  finalFloodCertificate: string;
  elmsurePaidReceipt: string;
  isInsuranceApproved: boolean;
  insuranceApprovalErrors: string[];
  guidelinesData: any;
  unclassifiedDocuments: any[];
  quoteOptions: any;
  insuranceCarrierStore: InsuranceCarrierStore;
  agentDirectoryStore: InsuranceAgentDirectoryStore;

  abstract getSelectedQuotesByLoanId();

  abstract getAvailableQuotesByLoanId();

  abstract getPropertiesMissingQuotes();

  abstract uploadInsuranceQuoteDocuments(documents);

  abstract saveInsuranceQuoteDetails(quote, formSave?: boolean);

  abstract downloadInsuranceQuoteDocument(currentDocument);

  abstract getLoanDetails(loanId);

  abstract getPropertiesOptions();

  abstract validateQuote(quote: any);

  abstract requestQuoteFromElmsure();

  abstract deleteQuoteDocument(quoteDocumentId: number);

  abstract deleteQuote(quoteId: number);

  abstract approveInsurance();

  abstract finalApproveInsurance();

  abstract revertInsurance();

  abstract resendElmsureQuote();

  abstract getInsuranceGuidelinesData();

  abstract getFinalDocuments();

  abstract refreshQuotes();

  abstract getUnclassifiedDocuments();

  abstract generateInvoiceDocument();

  abstract createFinalDocument(
    type: FinalDocumentType,
    insuranceQuoteDocumentIds: number[]
  );

  abstract downloadFinalDocument(type: FinalDocumentType);
  abstract downloadUnclassifiedDocument(
    loanId: number,
    documentId: number,
    documentName: string
  );
  abstract deleteUnclassifiedDocument(documentId: number);
  abstract classifiedUnclassifiedDocument(
    documentId: number,
    insuranceQuoteDetailId: number
  );

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.finalDocumentSelectionGridStore = new GridStore(() => this.initGrid());
    this.insuranceCarrierStore = new InsuranceCarrierStore(globalStore);
    this.agentDirectoryStore = new InsuranceAgentDirectoryStore(globalStore);

    this.setDefaults();
    makeObservable(this, {
      isSaveInsuranceQuoteActive: observable,
      quotes: observable,
      finalChoiceQuotes: observable,
      propertiesMissingQuotes: observable,
      propertiesOptions: observable,
      currentQuote: observable,
      isPdfPreviewModalOpen: observable,
      pdfTitle: observable,
      pdfData: observable,
      uploadDocuments: observable,
      viewDetails: observable,
      selectQouteConfirm: observable,
      setDefaults: action,
      reset: action,
      setQuotes: action,
      setFinalChoiceQuotes: action,
      getPropertiesOptions: action,
      handleOpenSave: action,
      handleCloseSave: action,
      handleUpload: action,
      generateInvoice: flow,
      generateInvoiceDocument: flow,
      handleSelectAsFinalChoice: flow,
      confirmSelectAsFinalChoise: flow,
      init: flow,
      getAvailableQuotesByLoanId: flow,
      getSelectedQuotesByLoanId: flow,
      saveInsuranceQuoteDetails: flow,
      getPropertiesMissingQuotes: flow,
      uploadInsuranceQuoteDocuments: flow,
      downloadInsuranceQuoteDocument: flow,
      getLoanDetails: flow,
      requestQuoteFromElmsure: flow,
      validateElmsureQuoteSubmission: action,
      deleteQuoteDocument: flow,
      deleteQuote: flow,
      approveInsurance: action,
      revertInsurance: flow,
      handleResendElmsureModal: action,
      resendElmsureQuoteModal: observable,
      resendElmsureQuote: flow,
      refreshQuotes: flow,
      initGrid: action,
      selectedRecords: observable,
      setSelectedRecords: action,
      createFinalDocument: flow,
      getFinalDocuments: flow,
      downloadFinalDocument: flow,
      createFinalBinder: flow,
      createFinalInvoice: flow,
      loadFinalDocuments: action,
      finalInvoice: observable,
      finalBinder: observable,
      finalFloodCertificate: observable,
      isInsuranceApproved: observable,
      insuranceApprovalErrors: observable,
      finalApproveInsurance: flow,
      getInsuranceGuidelinesData: flow,
      guidelinesData: observable,
      downloadUnclassifiedDocument: flow,
      getUnclassifiedDocuments: flow,
      loadUnclassifiedDocuments: action,
      unclassifiedDocuments: observable,
      deleteUnclassifiedDocument: flow,
      classifiedUnclassifiedDocument: flow,
      quoteOptions: observable,
      updateClassification: action,
      elmsurePaidReceipt: observable,
      totalInsurancePremium: computed,
    });
  }

  setDefaults() {
    this.isSaveInsuranceQuoteActive = false;
    this.setQuotes([]);
    this.setFinalChoiceQuotes([]);
    this.propertiesMissingQuotes = [];
    this.insuranceApprovalErrors = [];
    this.propertiesOptions = [];
    this.currentQuote = {};
    this.selectedRecords = [];
    this.finalInvoice = '';
    this.finalBinder = '';
    this.finalElmsureBinder = '';
    this.finalElmsureInvoice = '';
    this.elmsurePaidReceipt = '';
    this.quoteOptions = [];
  }

  reset() {
    this.setDefaults();
    this.insuranceQuoteFormStore.reset();
    this.finalDocumentSelectionGridStore.reset();
    this.agentDirectoryStore.reset();
  }

  setQuotes(quotes) {
    this.quotes = quotes;
  }

  setFinalChoiceQuotes(finalChoiceQuotes) {
    this.finalChoiceQuotes = finalChoiceQuotes;
  }

  setSelectedRecords(record, selected: boolean) {
    if (selected) {
      this.selectedRecords.push(record);
    }
    if (record != null && !selected) {
      this.selectedRecords = this.selectedRecords.filter(
        item =>
          item.insuranceQuoteDocumentId !== record.insuranceQuoteDocumentId
      );
    }
  }

  *init(loanId: string, taskId: string) {
    this.reset();
    this.loanId = loanId;
    this.taskId = taskId;
    yield this.getLoanDetails(this.loanId);
    yield this.getAvailableQuotesByLoanId();
    yield this.getSelectedQuotesByLoanId();
    yield this.getPropertiesMissingQuotes();
    this.getPropertiesOptions();
    this.resendElmsureQuoteStore.setDefaultBorrowerEmailAddress();
    this.getInsuranceGuidelinesData();
  }

  *initReviewForm(loanId: string, taskId: string) {
    this.reset();
    this.loanId = loanId;
    this.taskId = taskId;
    yield this.getLoanDetails(this.loanId);
  }

  handleOpenSave(index = undefined, selectedQuote = false) {
    this.insuranceQuoteFormStore.reset();
    this.getPropertiesOptions();
    if (isNil(index)) {
      this.currentQuote = {};
    } else {
      this.currentQuote = selectedQuote
        ? this.finalChoiceQuotes[index]
        : this.quotes[index];
    }
    this.opentInsuranceModal(this.currentQuote);
  }

  handleOpenViewDetails(index: number, isSelected: boolean) {
    this.currentQuote = isSelected
      ? this.finalChoiceQuotes[index]
      : this.quotes[index];
    this.viewDetails = true;
  }

  handleOpenConfirmSelectQoute(index: number, isSelected: boolean) {
    this.currentQuote = isSelected
      ? this.quotes[index]
      : this.finalChoiceQuotes[index];
    this.currentQuote.selectedQuote = isSelected;
    this.selectQouteConfirm = true;
  }

  *confirmSelectAsFinalChoise() {
    yield this.saveInsuranceQuoteDetails(this.currentQuote);
    this.selectQouteConfirm = false;
  }

  opentInsuranceModal(quote) {
    this.currentQuote = quote;
    this.agentDirectoryStore.reset();
    this.insuranceQuoteFormStore.loadForm(quote);
    this.isSaveInsuranceQuoteActive = true;
  }

  handleResendElmsureModal(isOpen: boolean) {
    this.resendElmsureQuoteModal = isOpen;
  }

  handleCloseSave() {
    this.isSaveInsuranceQuoteActive = false;
  }

  *handleSelectAsFinalChoice(index, selected: boolean) {
    this.currentQuote = selected
      ? this.quotes[index]
      : this.finalChoiceQuotes[index];
    this.currentQuote.selectedQuote = selected;
    yield this.saveInsuranceQuoteDetails(this.currentQuote);
  }

  handleUpload(index: number, isSelected: boolean) {
    this.currentQuote = isSelected
      ? this.finalChoiceQuotes[index]
      : this.quotes[index];
    this.uploadDocuments = true;
  }

  *generateInvoice(index: number, isSelected: boolean) {
    if (isSelected) {
      this.currentQuote = this.finalChoiceQuotes[index];
      yield this.generateInvoiceDocument();
      yield this.getSelectedQuotesByLoanId();
    }
  }

  validateAndSaveQuote(quote: any) {
    if (this.validateQuote(quote)) {
      Promise.resolve(this.saveInsuranceQuoteDetails(quote, true)).then(() => {
        this.agentDirectoryStore.invalidate();
      });
    }
  }

  validateElmsureQuoteSubmission(): boolean {
    if (
      this.insuranceQuoteFormStore.getValue('properties').length > 0 &&
      (this.insuranceQuoteFormStore.getValue('generalLiabilityCoverage') ||
        this.insuranceQuoteFormStore.getValue('propertyCoverage') ||
        this.insuranceQuoteFormStore.getValue('floodCoverage'))
    ) {
      return true;
    }
    this.globalStore.notificationStore.showWarningNotification({
      message: 'Please fill the required information',
    });
    return false;
  }

  handleQuoteRowExpansion = (quote, fallbackIndex: number) => {
    const currentQuoteId = quote?.insuranceQuoteDetailId;
    const index = this.quotes.findIndex(
      q => q.insuranceQuoteDetailId === currentQuoteId
    );
    const reviewIndex = index >= 0 ? index : fallbackIndex;
    this.handleOpenSave(reviewIndex, quote?.selectedQuote);
  };

  async initGrid() {
    const rows = _.flatten(
      this.finalChoiceQuotes.map(quote => quote.documents)
    );
    return new ApiResponse({ data: { rows: rows } }, {});
  }

  async loadFinalDocuments() {
    const { data } = await this.getFinalDocuments();
    this.finalBinder = data?.finalBinderPath;
    this.finalInvoice = data?.finalInvoicePath;
    this.finalElmsureBinder = data?.elmsureFinalBinderPath;
    this.finalElmsureInvoice = data?.elmsureFinalInvoicePath;
    this.finalFloodCertificate = data?.finalFloodCertificatePath;
    this.isInsuranceApproved = data?.insuranceApproved;
    this.elmsurePaidReceipt = data?.elmsurePaidReceiptPath;
  }

  async loadUnclassifiedDocuments() {
    const { data } = await this.getUnclassifiedDocuments();
    this.unclassifiedDocuments = data.data;
  }

  async createFinalBinder() {
    const { data } = await this.createFinalDocument(
      FinalDocumentType.BINDER,
      this.selectedRecords.map(record => record.insuranceQuoteDocumentId)
    );
    this.finalBinder = data.finalBinderPath;
  }

  async createFinalInvoice() {
    const { data } = await this.createFinalDocument(
      FinalDocumentType.INVOICE,
      this.selectedRecords.map(record => record.insuranceQuoteDocumentId)
    );
    this.finalInvoice = data.finalInvoicePath;
  }

  createOptionQuotes(list: any) {
    const newQuoteOptions = list
      .filter(quote => !quote.quoteShowId.toLowerCase().includes('elmsure'))
      .map(quote => ({
        id: quote.insuranceQuoteDetailId,
        label: quote.quoteShowId,
      }));
    const existingQuotes = new Set(this.quoteOptions.map(option => option.id));
    const newQuotes = newQuoteOptions.filter(
      option => !existingQuotes.has(option.id)
    );
    this.quoteOptions.push(...newQuotes);
  }

  updateClassification(
    unclassifiedDocumentId: number,
    classificationId: number
  ) {
    const document = this.unclassifiedDocuments.find(
      document => document.unclassifiedDocumentId === unclassifiedDocumentId
    );
    if (document) {
      document.classificationId = classificationId;
    }
  }

  get totalInsurancePremium() {
    return this.finalChoiceQuotes.reduce((total, quote) => {
      return total + (quote?.insurancePremium || 0);
    }, 0);
  }
}
